import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';
import { UAParser } from 'ua-parser-js';

export interface AnalyticsSettings {
  id: string;
  tracking_enabled: boolean;
  realtime_enabled: boolean;
  retention_days: number;
  updated_at: string;
}

export interface PageView {
  id: string;
  session_id: string | null;
  path: string;
  title: string | null;
  referrer: string | null;
  browser: string | null;
  browser_version: string | null;
  os: string | null;
  os_version: string | null;
  device_type: string;
  device_vendor: string | null;
  device_model: string | null;
  screen_width: number | null;
  screen_height: number | null;
  duration_ms: number | null;
  ip_address: string | null;
  user_id: string | null;
  locale: string | null;
  language: string | null;
  created_at: string;
}

export interface VisitorSession {
  id: string;
  started_at: string;
  last_seen_at: string;
  page_count: number;
  browser: string | null;
  browser_version: string | null;
  os: string | null;
  os_version: string | null;
  device_type: string;
  device_vendor: string | null;
  device_model: string | null;
  screen_width: number | null;
  screen_height: number | null;
  entry_path: string | null;
  exit_path: string | null;
  referrer: string | null;
  is_returning: boolean;
  user_id: string | null;
  ip_address: string | null;
  country: string | null;
  city: string | null;
  region: string | null;
  latitude: number | null;
  longitude: number | null;
  timezone: string | null;
  locale: string | null;
  language: string | null;
}

export interface AnalyticsEvent {
  id: string;
  session_id: string | null;
  event_name: string;
  event_data: Record<string, unknown>;
  path: string | null;
  user_id: string | null;
  created_at: string;
}

export interface GeoInfo {
  ip: string;
  country_name: string | null;
  city: string | null;
  region: string | null;
  latitude: number | null;
  longitude: number | null;
  timezone: string | null;
}

const SESSION_KEY = 'analytics_session_id';
const VISITOR_KEY = 'analytics_returning';

function getLocaleInfo(): { locale: string; language: string } {
  const locale = navigator.language || Intl.DateTimeFormat().resolvedOptions().locale || 'Unknown';
  const language = locale.split('-')[0] || 'Unknown';
  return { locale, language };
}

interface ParsedUA {
  browser: string;
  browser_version: string;
  os: string;
  os_version: string;
  device_type: string;
  device_vendor: string;
  device_model: string;
}

function parseUserAgent(ua: string): ParsedUA {
  const result = UAParser(ua);

  const browser = result.browser.name || 'Unknown';
  const browser_version = result.browser.major || result.browser.version?.split('.')[0] || '';

  const osName = result.os.name || 'Unknown';
  const os = osName === 'Mac OS' ? 'macOS' : osName;
  const os_version = result.os.version || '';

  const deviceType = result.device.type || 'desktop';
  const device_type = deviceType === 'mobile' ? 'mobile' : deviceType === 'tablet' ? 'tablet' : 'desktop';
  const device_vendor = result.device.vendor || 'Unknown';
  const device_model = result.device.model || '';

  return { browser, browser_version, os, os_version, device_type, device_vendor, device_model };
}

async function callTrackEndpoint(payload: Record<string, unknown>): Promise<void> {
  const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').trim();
  const anonKey = String(import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim();
  if (!supabaseUrl || !anonKey) return;
  try {
    await fetch(`${supabaseUrl}/functions/v1/track-analytics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // silently ignore — never break the app
  }
}

export async function fetchGeoInfo(): Promise<GeoInfo | null> {
  try {
    const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').trim();
    const supabaseAnonKey = String(import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim();
    if (!supabaseUrl || !supabaseAnonKey) return null;

    const res = await fetch(`${supabaseUrl}/functions/v1/geo-lookup`, {
      headers: {
        Authorization: `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.error) return null;
    return {
      ip: data.ip ?? null,
      country_name: data.country_name ?? null,
      city: data.city ?? null,
      region: data.region ?? null,
      latitude: data.latitude ?? null,
      longitude: data.longitude ?? null,
      timezone: data.timezone ?? null,
    };
  } catch {
    return null;
  }
}

export function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

export function isReturningVisitor(): boolean {
  return localStorage.getItem(VISITOR_KEY) === '1';
}

export function markAsReturning(): void {
  localStorage.setItem(VISITOR_KEY, '1');
}

export async function loadAnalyticsSettings(): Promise<AnalyticsSettings | null> {
  const { data, error } = await supabase
    .from('analytics_settings')
    .select('*')
    .maybeSingle();
  if (error) return null;
  return data;
}

export async function saveAnalyticsSettings(
  id: string,
  settings: Partial<Pick<AnalyticsSettings, 'tracking_enabled' | 'realtime_enabled' | 'retention_days'>>
): Promise<void> {
  await supabase
    .from('analytics_settings')
    .update({ ...settings, updated_at: new Date().toISOString() })
    .eq('id', id);
}

export async function startSession(path: string): Promise<string | null> {
  const parsed = parseUserAgent(navigator.userAgent);
  const sessionId = getOrCreateSessionId();
  const { locale, language } = getLocaleInfo();

  markAsReturning();

  await callTrackEndpoint({
    type: 'session_start',
    session_id: sessionId,
    path,
    referrer: document.referrer || null,
    is_returning: isReturningVisitor(),
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    locale,
    language,
    browser: parsed.browser,
    browser_version: parsed.browser_version || null,
    os: parsed.os,
    os_version: parsed.os_version || null,
    device_type: parsed.device_type,
    device_vendor: parsed.device_vendor || null,
    device_model: parsed.device_model || null,
  });

  return sessionId;
}

export async function updateSessionExit(sessionId: string, exitPath: string): Promise<void> {
  await callTrackEndpoint({
    type: 'session_update',
    session_id: sessionId,
    path: exitPath,
    exit_path: exitPath,
  });
}

export async function recordPageView(
  sessionId: string | null,
  path: string,
  durationMs?: number
): Promise<void> {
  const parsed = parseUserAgent(navigator.userAgent);
  const { locale, language } = getLocaleInfo();

  await callTrackEndpoint({
    type: 'page_view',
    session_id: sessionId ?? getOrCreateSessionId(),
    path,
    title: document.title || null,
    referrer: document.referrer || null,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    duration_ms: durationMs ?? null,
    locale,
    language,
    browser: parsed.browser,
    browser_version: parsed.browser_version || null,
    os: parsed.os,
    os_version: parsed.os_version || null,
    device_type: parsed.device_type,
    device_vendor: parsed.device_vendor || null,
    device_model: parsed.device_model || null,
  });
}

export async function trackEvent(
  sessionId: string | null,
  eventName: string,
  eventData: Record<string, unknown> = {},
  path?: string
): Promise<void> {
  await supabase.from('analytics_events').insert({
    session_id: sessionId,
    event_name: eventName,
    event_data: eventData,
    path: path ?? window.location.pathname,
  });
}

function startOfDayNDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - (days - 1));
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export async function fetchPageViewStats(days = 30) {
  const since = startOfDayNDaysAgo(days);
  let allData: { id: string; created_at: string }[] = [];
  let from = 0;
  const pageSize = 1000;
  while (true) {
    const { data, error } = await supabase
      .from('page_views')
      .select('id, created_at')
      .gte('created_at', since)
      .order('created_at', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error || !data || data.length === 0) break;
    allData = allData.concat(data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return allData;
}

export async function fetchSessionStats(days = 30) {
  const since = startOfDayNDaysAgo(days);
  let allData: VisitorSession[] = [];
  let from = 0;
  const pageSize = 1000;
  while (true) {
    const { data, error } = await supabase
      .from('visitor_sessions')
      .select('id, started_at, last_seen_at, page_count, device_type, browser, browser_version, os, os_version, device_vendor, device_model, is_returning, country, city, region, ip_address, timezone, latitude, longitude')
      .gte('started_at', since)
      .order('started_at', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error || !data || data.length === 0) break;
    allData = allData.concat(data as VisitorSession[]);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return allData;
}

export async function fetchRecentPageViews(limit = 50) {
  const { data } = await supabase
    .from('page_views')
    .select('id, session_id, path, title, referrer, browser, browser_version, os, os_version, device_type, device_vendor, device_model, screen_width, screen_height, duration_ms, ip_address, user_id, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  return data ?? [];
}

export async function fetchRecentSessions(limit = 20) {
  const { data } = await supabase
    .from('visitor_sessions')
    .select('id, started_at, last_seen_at, page_count, device_type, browser, browser_version, os, os_version, device_vendor, device_model, is_returning, country, city, region, ip_address, timezone, latitude, longitude, entry_path, exit_path, referrer, screen_width, screen_height, user_id')
    .order('last_seen_at', { ascending: false })
    .limit(limit);
  return data ?? [];
}

export async function fetchTopPages(days = 30, limit = 10) {
  const since = startOfDayNDaysAgo(days);
  let allPaths: { path: string }[] = [];
  let from = 0;
  const pageSize = 1000;
  while (true) {
    const { data, error } = await supabase
      .from('page_views')
      .select('path')
      .gte('created_at', since)
      .range(from, from + pageSize - 1);
    if (error || !data || data.length === 0) break;
    allPaths = allPaths.concat(data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  const counts: Record<string, number> = {};
  allPaths.forEach(r => { counts[r.path] = (counts[r.path] ?? 0) + 1; });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([path, views]) => ({ path, views }));
}

export async function fetchPageViewCount(days = 30): Promise<number> {
  const since = startOfDayNDaysAgo(days);
  const { count } = await supabase
    .from('page_views')
    .select('*', { count: 'exact', head: true })
    .gte('created_at', since);
  return count ?? 0;
}

export async function fetchEventStats(days = 30) {
  const since = startOfDayNDaysAgo(days);
  const { data } = await supabase
    .from('analytics_events')
    .select('event_name, created_at')
    .gte('created_at', since);
  return data ?? [];
}
